import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import Loader from '@/components/common/Loader';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import Error from '@/components/common/Error';
import Spinner from '@/components/common/Spinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import HorizontalLogo from '@/components/common/HorizontalLogo';

import { useSelf } from '@/data/user';
import { useTenantsForUser } from '@/data/tenantUser';
import { setTenantId } from '@/helpers/api';

const formSchema = z.object({
  tenantId: z.string().min(1, { message: 'Tenant selection is required' })
});

const TenantForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const navigate = useNavigate();
  const user = useSelf();
  const tenantUsers = useTenantsForUser();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { tenant: '' }
  });

  const onSubmit = async (values) => {
    setApiError('');
    setIsSubmitting(true);
    try {
      setTenantId(values.tenantId);
      setIsSubmitting(false);
      navigate('/');
    } catch (err) {
      setIsSubmitting(false);
      setApiError(err.message);
    }
    navigate('/');
    setIsSubmitting(false);
  };

  if (
    (user.isLoading && !user.data) ||
    (tenantUsers.isLoading && !tenantUsers.data)
  ) {
    return (
      <Card className="w-screen max-w-[380px]">
        <CardContent className="h-50">
          <Loader />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-screen max-w-[380px]">
      <CardHeader>
        <div className="p-6">
          <HorizontalLogo />
        </div>
        <CardTitle>Select Tenant</CardTitle>
        <CardDescription>Choose your tenant</CardDescription>
      </CardHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-2">
            <Select onValueChange={(value) => form.setValue('tenantId', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenantUsers.data &&
                  tenantUsers.data.map((tenantUser) => (
                    <SelectItem key={tenantUser.id} value={tenantUser.tenantId}>
                      {tenantUser.tenant.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {apiError && <Error error={apiError} />}
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting && <Spinner />}
              Select Tenant
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default TenantForm;
